import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminAPI, api } from '../../api/client'
import { useAuthStore } from '../../store/authStore'

// ─── Gen Item Preview (shared helper) ────────────────────────────────────────

function stripFences(raw: string): string {
  return raw.replace(/^```(?:json)?[\r\n]*/i, '').replace(/[\r\n]*```\s*$/, '').trim()
}

function safeParse(payload: unknown): any {
  // Already an object (Oracle may return parsed JSON directly)
  if (payload !== null && typeof payload === 'object') {
    const obj = payload as any
    if (obj?.error === 'parse_failed' && typeof obj.raw === 'string') {
      try { return JSON.parse(stripFences(obj.raw)) } catch { return obj }
    }
    return obj
  }
  // String payload
  try {
    const parsed = JSON.parse(payload as string)
    if (parsed?.error === 'parse_failed' && typeof parsed.raw === 'string') {
      try { return JSON.parse(stripFences(parsed.raw)) } catch { return parsed }
    }
    return parsed
  } catch { return {} }
}

function GenItemPreview({ parsed, contentType }: { parsed: any; contentType?: string }) {
  if (!parsed || typeof parsed !== 'object') return null

  switch (contentType) {
    case 'reading_passage':
      return (
        <div>
          {parsed.title && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.title}</strong>}
          {parsed.passage
            ? <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{String(parsed.passage).slice(0, 250)}…</p>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có nội dung passage</p>
          }
          {parsed.questions?.length > 0 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>{parsed.questions.length} câu hỏi</p>}
        </div>
      )
    case 'listening_script':
      return (
        <div>
          {parsed.title && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.title}</strong>}
          {parsed.script
            ? <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{String(parsed.script).slice(0, 250)}…</p>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có script</p>
          }
          {parsed.questions?.length > 0 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>{parsed.questions.length} câu hỏi</p>}
        </div>
      )
    case 'writing_prompt':
      return (
        <div>
          {parsed.task_type && <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#004be2', marginBottom: '0.25rem', display: 'block' }}>{parsed.task_type}</span>}
          {parsed.prompt
            ? <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{String(parsed.prompt).slice(0, 250)}</p>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có prompt</p>
          }
          {parsed.word_count > 0 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>~{parsed.word_count} từ</p>}
        </div>
      )
    case 'speaking_prompt':
      return (
        <div>
          {parsed.part && <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#006571', marginBottom: '0.25rem', display: 'block' }}>{parsed.part}</span>}
          {parsed.topic && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.topic}</strong>}
          {parsed.outline_hints?.length > 0 && <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{(parsed.outline_hints as string[]).slice(0, 3).join(' · ')}</p>}
          {!parsed.topic && !parsed.outline_hints?.length && <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có nội dung</p>}
        </div>
      )
    case 'grammar_drill':
      return (
        <div>
          {parsed.topic && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.topic}</strong>}
          {parsed.questions?.length > 0
            ? (parsed.questions as any[]).slice(0, 2).map((q: any, i: number) => (
                <p key={i} style={{ margin: '0.125rem 0', color: '#535b71', fontSize: '0.875rem' }}>{i + 1}. {String(q.prompt ?? '').slice(0, 120)}</p>
              ))
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có câu hỏi</p>
          }
          {parsed.questions?.length > 2 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>+{parsed.questions.length - 2} câu nữa</p>}
        </div>
      )
    case 'vocabulary_drill': {
      // Normalize: handle array directly, or alternate keys from AI
      const words = parsed.words ?? parsed.vocabulary ?? parsed.items ?? parsed.word_list
        ?? (Array.isArray(parsed) ? parsed : null)
      return (
        <div>
          {words?.length > 0
            ? <>
                {(words as any[]).slice(0, 5).map((w: any, i: number) => (
                  <span key={i} style={{ display: 'inline-block', margin: '0.125rem', padding: '0.125rem 0.5rem', background: '#eef0ff', borderRadius: '9999px', fontSize: '0.8125rem', fontWeight: 600, color: '#004be2' }}>{w.word}</span>
                ))}
                {words.length > 5 && <span style={{ fontSize: '0.75rem', color: '#a5adc6' }}> +{words.length - 5} từ</span>}
              </>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có từ vựng</p>
          }
        </div>
      )
    }
    default:
      return <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{parsed.title ?? parsed.prompt ?? parsed.topic ?? contentType ?? '—'}</p>
  }
}

// ─── AI Provider Cost Control ─────────────────────────────────────────────────

export function AICostControlTab() {
  const qc = useQueryClient()
  const [form, setForm] = useState({ module: '', dailyBudgetUsd: '', monthlyBudgetUsd: '', preferredProvider: 'bedrock', fallbackProvider: 'openai', autoSwitch: true })
  const { data: limits } = useQuery({ queryKey: ['admin-ai-limits'], queryFn: () => adminAPI.getAICosts(1) })
  const { data: credit } = useQuery({ queryKey: ['admin-aws-credit'], queryFn: adminAPI.getAWSCredit })

  const saveBudget = useMutation({
    mutationFn: () => api.post('/admin/ai-config/budget', { module: form.module, dailyBudgetUsd: Number(form.dailyBudgetUsd), monthlyBudgetUsd: Number(form.monthlyBudgetUsd), preferredProvider: form.preferredProvider, fallbackProvider: form.fallbackProvider, autoSwitch: form.autoSwitch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-ai-limits'] }),
  })

  const MODULES = ['writing_scorer', 'speaking_content', 'pronunciation', 'vocab_gen', 'content_gen', 'diagnostic']
  const PROVIDERS = ['bedrock', 'openai', 'anthropic']

  return (
    <div className="admin-tab-panel">
      <h2>AI Provider Cost Control</h2>

      {credit && (
        <div className={`credit-card ${credit.isWarning ? 'warning' : ''}`} style={{ marginBottom: '1.5rem' }}>
          <strong>AWS Credit còn lại: ${Number(credit.estimatedRemaining).toFixed(2)}</strong>
          <span> / ${credit.initialCredit} ban đầu</span>
          {credit.isWarning && <span className="badge-warning"> ⚠️ Gần hết — auto-switch khi &lt;${credit.autoSwitchThreshold}</span>}
        </div>
      )}

      <div className="admin-form" style={{ marginBottom: '2rem' }}>
        <h3>Đặt ngân sách theo module</h3>
        <div className="form-grid">
          <label>Module
            <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}>
              <option value="">-- Chọn module --</option>
              {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Daily budget (USD)
            <input type="number" step="0.01" value={form.dailyBudgetUsd} onChange={e => setForm({ ...form, dailyBudgetUsd: e.target.value })} placeholder="0.50" />
          </label>
          <label>Monthly budget (USD)
            <input type="number" step="0.1" value={form.monthlyBudgetUsd} onChange={e => setForm({ ...form, monthlyBudgetUsd: e.target.value })} placeholder="10.00" />
          </label>
          <label>Provider ưu tiên
            <select value={form.preferredProvider} onChange={e => setForm({ ...form, preferredProvider: e.target.value })}>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label>Fallback provider
            <select value={form.fallbackProvider} onChange={e => setForm({ ...form, fallbackProvider: e.target.value })}>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="toggle-row">
            <span>Auto-switch khi vượt ngân sách</span>
            <input type="checkbox" checked={form.autoSwitch} onChange={e => setForm({ ...form, autoSwitch: e.target.checked })} />
          </label>
        </div>
        <button className="btn-primary" onClick={() => saveBudget.mutate()} disabled={!form.module || saveBudget.isPending}>
          {saveBudget.isPending ? 'Đang lưu...' : 'Lưu cấu hình'}
        </button>
      </div>

      <div className="stats-section">
        <h3>Chi phí hôm nay theo module</h3>
        <table className="admin-table">
          <thead><tr><th>Module</th><th>Provider</th><th>Requests</th><th>Tokens IN</th><th>Tokens OUT</th><th>Chi phí ($)</th><th>% Budget</th></tr></thead>
          <tbody>
            {(limits ?? []).map((row: any[], i: number) => (
              <tr key={i}>
                <td>{row[1]}</td><td>{row[2]}</td><td>{row[3]}</td>
                <td>{row[4]}</td><td>{row[5]}</td>
                <td>${Number(row[6] ?? 0).toFixed(5)}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ flex: 1, height: '0.375rem', background: '#eef0ff', borderRadius: '9999px', overflow: 'hidden', minWidth: '4rem' }}>
                      <div style={{ width: `${Math.min(100, (Number(row[6]) / 0.5) * 100)}%`, height: '100%', background: Number(row[6]) > 0.4 ? '#b41340' : '#004be2', borderRadius: '9999px' }} />
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>{((Number(row[6]) / 0.5) * 100).toFixed(0)}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── AI Request Logs ──────────────────────────────────────────────────────────

export function AIRequestLogsTab() {
  const [filters, setFilters] = useState({ module: '', provider: '', page: 1 })
  const { data: logs, isLoading } = useQuery({
    queryKey: ['admin-ai-logs-detail', filters],
    queryFn: () => adminAPI.getAILogs(filters),
  })

  const MODULES = ['', 'writing_scorer', 'speaking_content', 'pronunciation', 'vocab_gen', 'content_gen', 'diagnostic']
  const PROVIDERS = ['', 'bedrock', 'openai', 'anthropic']

  return (
    <div className="admin-tab-panel">
      <h2>AI Request Logs</h2>
      <div className="admin-toolbar" style={{ marginBottom: '1rem' }}>
        <select value={filters.module} onChange={e => setFilters({ ...filters, module: e.target.value, page: 1 })}>
          {MODULES.map(m => <option key={m} value={m}>{m || 'Tất cả module'}</option>)}
        </select>
        <select value={filters.provider} onChange={e => setFilters({ ...filters, provider: e.target.value, page: 1 })}>
          {PROVIDERS.map(p => <option key={p} value={p}>{p || 'Tất cả provider'}</option>)}
        </select>
      </div>

      {isLoading ? <div className="loading">Đang tải...</div> : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Thời gian</th><th>Module</th><th>Provider</th><th>Model</th>
              <th>Tokens IN</th><th>Tokens OUT</th><th>Chi phí ($)</th>
              <th>Latency (ms)</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(logs ?? []).map((row: any[], i: number) => (
              <tr key={i}>
                <td style={{ whiteSpace: 'nowrap' }}>{row[11] ? new Date(row[11]).toLocaleString('vi') : '—'}</td>
                <td>{row[2]}</td>
                <td><span className="ai-provider-badge">{row[3]}</span></td>
                <td style={{ fontSize: '0.75rem', color: '#535b71' }}>{row[4]}</td>
                <td>{row[5]?.toLocaleString()}</td>
                <td>{row[6]?.toLocaleString()}</td>
                <td>${Number(row[7] ?? 0).toFixed(6)}</td>
                <td>{row[8]}</td>
                <td>
                  <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700, background: row[9] === 'success' ? 'rgba(0,101,113,0.1)' : 'rgba(180,19,64,0.1)', color: row[9] === 'success' ? '#006571' : '#b41340' }}>
                    {row[9] ?? 'success'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pagination">
        <button disabled={filters.page === 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>← Trang trước</button>
        <span>Trang {filters.page}</span>
        <button onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Trang sau →</button>
      </div>
    </div>
  )
}

// ─── AI System Cost Monitor ───────────────────────────────────────────────────

export function AISystemCostMonitorTab() {
  const [days, setDays] = useState(30)
  const { data: costs } = useQuery({ queryKey: ['admin-ai-costs-full', days], queryFn: () => adminAPI.getAICosts(days) })
  const { data: health } = useQuery({ queryKey: ['admin-health'], queryFn: adminAPI.getSystemHealth })
  const { data: credit } = useQuery({ queryKey: ['admin-aws-credit'], queryFn: adminAPI.getAWSCredit })

  // Aggregate by day
  const byDay: Record<string, number> = {}
  ;(costs ?? []).forEach((row: any[]) => {
    const day = row[0] ? new Date(row[0]).toLocaleDateString('vi') : '?'
    byDay[day] = (byDay[day] ?? 0) + Number(row[6] ?? 0)
  })
  const dailyEntries = Object.entries(byDay).slice(-14)
  const maxCost = Math.max(...dailyEntries.map(([, v]) => v), 0.001)

  // Aggregate by provider
  const byProvider: Record<string, number> = {}
  ;(costs ?? []).forEach((row: any[]) => {
    const p = row[2] ?? 'unknown'
    byProvider[p] = (byProvider[p] ?? 0) + Number(row[6] ?? 0)
  })

  const totalCost = Object.values(byProvider).reduce((a, b) => a + b, 0)

  return (
    <div className="admin-tab-panel">
      <h2>AI System Cost Monitor</h2>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, color: '#535b71', fontSize: '0.875rem' }}>Khoảng thời gian:</span>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={days === d ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.375rem 0.875rem', fontSize: '0.8125rem' }}>
            {d} ngày
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="monitor-grid" style={{ marginBottom: '2rem' }}>
        <div className="monitor-card">
          <div className="monitor-label">Tổng chi phí AI</div>
          <div className="monitor-value">${totalCost.toFixed(4)}</div>
        </div>
        <div className="monitor-card">
          <div className="monitor-label">TB / ngày</div>
          <div className="monitor-value">${(totalCost / days).toFixed(5)}</div>
        </div>
        <div className="monitor-card">
          <div className="monitor-label">AI requests hôm nay</div>
          <div className="monitor-value">{health?.aiRequestsToday ?? '—'}</div>
        </div>
        <div className="monitor-card">
          <div className="monitor-label">Chi phí hôm nay</div>
          <div className="monitor-value">${Number(health?.aiCostToday ?? 0).toFixed(4)}</div>
        </div>
        {credit && (
          <div className={`monitor-card ${credit.isWarning ? 'monitor-error' : ''}`}>
            <div className="monitor-label">AWS Credit còn lại</div>
            <div className="monitor-value">${Number(credit.estimatedRemaining).toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* Cost by provider */}
      <div className="stats-section" style={{ marginBottom: '2rem' }}>
        <h3>Chi phí theo provider</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {Object.entries(byProvider).map(([provider, cost]) => (
            <div key={provider} style={{ background: 'white', borderRadius: '1rem', padding: '1.25rem 1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.06)', minWidth: '10rem' }}>
              <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#535b71', marginBottom: '0.5rem' }}>{provider}</span>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.5rem', fontWeight: 900, color: '#004be2' }}>${cost.toFixed(4)}</span>
              <span style={{ display: 'block', fontSize: '0.75rem', color: '#a5adc6', marginTop: '0.25rem' }}>{totalCost > 0 ? ((cost / totalCost) * 100).toFixed(1) : 0}% tổng</span>
            </div>
          ))}
        </div>
      </div>

      {/* Daily bar chart */}
      <div className="stats-section" style={{ marginBottom: '2rem' }}>
        <h3>Chi phí theo ngày (14 ngày gần nhất)</h3>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: '10rem', padding: '0 0.5rem' }}>
          {dailyEntries.map(([day, cost]) => (
            <div key={day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
              <span style={{ fontSize: '0.625rem', color: '#535b71', fontWeight: 700 }}>${cost.toFixed(4)}</span>
              <div style={{ width: '100%', background: 'linear-gradient(to top, #004be2, #809bff)', borderRadius: '0.375rem 0.375rem 0 0', height: `${(cost / maxCost) * 7}rem`, minHeight: '0.25rem', transition: 'height 0.4s' }} />
              <span style={{ fontSize: '0.5625rem', color: '#a5adc6', textAlign: 'center', lineHeight: 1.2 }}>{day.slice(0, 5)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Full table */}
      <div className="stats-section">
        <h3>Chi tiết theo module & provider</h3>
        <table className="admin-table">
          <thead><tr><th>Ngày</th><th>Module</th><th>Provider</th><th>Requests</th><th>Tokens IN</th><th>Tokens OUT</th><th>Chi phí ($)</th></tr></thead>
          <tbody>
            {(costs ?? []).slice(0, 50).map((row: any[], i: number) => (
              <tr key={i}>
                <td>{row[0] ? new Date(row[0]).toLocaleDateString('vi') : '—'}</td>
                <td>{row[1]}</td><td>{row[2]}</td><td>{row[3]}</td>
                <td>{Number(row[4] ?? 0).toLocaleString()}</td>
                <td>{Number(row[5] ?? 0).toLocaleString()}</td>
                <td>${Number(row[6] ?? 0).toFixed(6)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Batch Gen Job Status ─────────────────────────────────────────────────────

export function BatchGenJobStatusTab() {
  const qc = useQueryClient()
  const [activeJobId, setActiveJobId] = useState<string | null>(null)

  const { data: history, isLoading } = useQuery({
    queryKey: ['admin-gen-history'],
    queryFn: adminAPI.getContentGenHistory,
    refetchInterval: 10000,
  })

  const { data: activeJob } = useQuery({
    queryKey: ['admin-gen-job', activeJobId],
    queryFn: () => adminAPI.getContentGenJob(activeJobId!),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const job = (query.state.data as any)?.job
      // job[5] = status
      return job && (job[5] === 'DONE' || job[5] === 'FAILED') ? false : 3000
    },
  })

  const approveItem = useMutation({
    mutationFn: (itemId: string) => adminAPI.approveItem(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })
  const rejectItem = useMutation({
    mutationFn: (itemId: string) => adminAPI.rejectItem(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })
  const regenerateItem = useMutation({
    mutationFn: (itemId: string) => adminAPI.regenerateItem(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })
  const approveAll = useMutation({
    mutationFn: (jobId: string) => adminAPI.approveAllItems(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })

  const STATUS_COLOR: Record<string, string> = { DONE: '#006571', RUNNING: '#004be2', FAILED: '#b41340', PENDING: '#b45309' }

  return (
    <div className="admin-tab-panel">
      <h2>Batch Generation Job Status</h2>

      {isLoading ? <div className="loading">Đang tải...</div> : (
        <table className="admin-table" style={{ marginBottom: '2rem' }}>
          <thead><tr><th>Job ID</th><th>Loại</th><th>CEFR</th><th>Số lượng</th><th>Đã duyệt</th><th>Chi phí</th><th>Trạng thái</th><th>Ngày tạo</th><th></th></tr></thead>
          <tbody>
            {(history ?? []).map((row: any[], i: number) => {
              const [jobId, contentType, cefrLevel, , quantity, status, , itemsApproved, aiCost, createdAt] = row
              const color = STATUS_COLOR[status] ?? '#535b71'
              return (
                <tr key={i} style={{ background: activeJobId === jobId ? 'rgba(0,75,226,0.04)' : undefined }}>
                  <td><code style={{ fontSize: '0.75rem' }}>{String(jobId).slice(0, 8)}…</code></td>
                  <td>{contentType}</td><td>{cefrLevel}</td><td>{quantity}</td>
                  <td>{itemsApproved ?? 0}</td>
                  <td>${Number(aiCost ?? 0).toFixed(5)}</td>
                  <td>
                    <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700, background: `${color}15`, color }}>
                      {status}
                    </span>
                  </td>
                  <td>{createdAt ? new Date(createdAt).toLocaleDateString('vi') : '—'}</td>
                  <td><button className="btn-xs" onClick={() => setActiveJobId(activeJobId === jobId ? null : jobId)}>
                    {activeJobId === jobId ? 'Đóng' : 'Xem'}
                  </button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {activeJobId && activeJob && (() => {
        const job = (activeJob as any).job
        const items: any[][] = (activeJob as any).items ?? []
        // job columns: [id(0), content_type(1), cefr_level(2), topic(3), quantity(4), status(5), ...]
        const [, , , , , status] = job ?? []
        const pending = items.filter(r => r[5] === 'PENDING_REVIEW').length
        const approved = items.filter(r => r[5] === 'APPROVED').length

        return (
          <div className="gen-job-panel">
            <div className="job-status-bar">
              <strong>Job #{String(activeJobId).slice(0, 8)}</strong>
              <span className={`job-status ${status?.toLowerCase()}`}>{status}</span>
              <span>{approved}/{items.length} đã duyệt</span>
              {status === 'DONE' && pending > 0 && (
                <button className="btn-primary" onClick={() => approveAll.mutate(activeJobId)}>
                  ✅ Duyệt tất cả ({pending})
                </button>
              )}
            </div>
            <div className="gen-items-list">
              {items.map((row, i) => {
                // item columns: [id(0), content_type(1), cefr_level(2), topic(3), payload(4), status(5), created_at(6)]
                const [itemId, contentType, cefrLevel, topic, payload, itemStatus] = row
                const parsed = safeParse(payload)
                const isBroken = parsed?.error === 'parse_failed'
                return (
                  <div key={i} className={`gen-item ${itemStatus.toLowerCase()}`}>
                    <div className="gen-item-header">
                      <span>#{i + 1} — {cefrLevel}{topic ? ` / ${topic}` : ''}</span>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {isBroken && <span style={{ fontSize: '0.7rem', background: 'rgba(180,19,64,0.1)', color: '#b41340', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontWeight: 700 }}>parse_failed</span>}
                        <span className={`item-status ${itemStatus.toLowerCase()}`}>{itemStatus}</span>
                      </div>
                    </div>
                    <div className="gen-item-preview">
                      {isBroken
                        ? <p style={{ margin: 0, color: '#b41340', fontSize: '0.875rem', fontStyle: 'italic' }}>⚠️ AI trả về format không hợp lệ — cần regenerate</p>
                        : <GenItemPreview parsed={parsed} contentType={contentType} />
                      }
                    </div>
                    {itemStatus === 'PENDING_REVIEW' && (
                      <div className="gen-item-actions">
                        {isBroken ? (
                          <button className="btn-xs btn-warning"
                            disabled={regenerateItem.isPending}
                            onClick={() => regenerateItem.mutate(itemId)}>
                            🔄 {regenerateItem.isPending ? 'Đang tạo lại...' : 'Tạo lại'}
                          </button>
                        ) : (
                          <>
                            <button className="btn-xs btn-success" onClick={() => approveItem.mutate(itemId)}>✅ Duyệt</button>
                            <button className="btn-xs btn-danger" onClick={() => rejectItem.mutate(itemId)}>❌ Từ chối</button>
                            <button className="btn-xs btn-warning" onClick={() => regenerateItem.mutate(itemId)}>🔄 Tạo lại</button>
                          </>
                        )}
                      </div>
                    )}
                    {itemStatus === 'REJECTED' && (
                      <div className="gen-item-actions">
                        <button className="btn-xs btn-warning" onClick={() => regenerateItem.mutate(itemId)}>🔄 Tạo lại</button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ─── Content Review & Approval ────────────────────────────────────────────────

export function ContentReviewApprovalTab() {
  const qc = useQueryClient()
  const [skill, setSkill] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string | null>(null)

  const { data: content, isLoading } = useQuery({
    queryKey: ['admin-content-review', skill, page],
    queryFn: () => adminAPI.listContent({ skill: skill || undefined, page }),
  })

  const approveContent = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/content/${id}/approve`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-content-review'] }),
  })
  const rejectContent = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/content/${id}/reject`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-content-review'] }),
  })

  const rows: any[][] = content ?? []
  const selectedRow = selected ? rows.find(r => r[0] === selected) : null

  return (
    <div className="admin-tab-panel">
      <h2>Content Review & Approval</h2>
      <div className="admin-toolbar" style={{ marginBottom: '1rem' }}>
        <select value={skill} onChange={e => { setSkill(e.target.value); setPage(1) }}>
          <option value="">Tất cả kỹ năng</option>
          {['listening', 'reading', 'writing', 'speaking'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>
        <div>
          {isLoading ? <div className="loading">Đang tải...</div> : (
            <table className="admin-table">
              <thead><tr><th>Tiêu đề</th><th>Kỹ năng</th><th>Cấp độ</th><th>Nguồn</th><th>Trạng thái</th><th></th></tr></thead>
              <tbody>
                {rows.map((row: any[]) => {
                  const [id, source, title, , cefrLevel, skill, , , , , , , , , , status] = row
                  const isPending = !status || status === 'pending_review'
                  return (
                    <tr key={id} style={{ background: selected === id ? 'rgba(0,75,226,0.04)' : undefined }}>
                      <td className="table-title">{title}</td>
                      <td><span className={`skill-badge skill-${skill}`}>{skill}</span></td>
                      <td>{cefrLevel}</td>
                      <td>{source ?? '—'}</td>
                      <td>
                        <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700, background: isPending ? 'rgba(180,83,9,0.1)' : 'rgba(0,101,113,0.1)', color: isPending ? '#b45309' : '#006571' }}>
                          {isPending ? 'Chờ duyệt' : 'Đã duyệt'}
                        </span>
                      </td>
                      <td className="action-cell">
                        <button className="btn-xs" onClick={() => setSelected(selected === id ? null : id)}>Xem</button>
                        {isPending && <>
                          <button className="btn-xs btn-success" onClick={() => approveContent.mutate(id)}>✅</button>
                          <button className="btn-xs btn-danger" onClick={() => rejectContent.mutate(id)}>❌</button>
                        </>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <div className="pagination">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Trang trước</button>
            <span>Trang {page}</span>
            <button onClick={() => setPage(p => p + 1)}>Trang sau →</button>
          </div>
        </div>

        {selectedRow && (
          <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.06)', alignSelf: 'start', position: 'sticky', top: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>{selectedRow[2]}</h4>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <span className={`skill-badge skill-${selectedRow[5]}`}>{selectedRow[5]}</span>
              <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', background: '#eef0ff', fontSize: '0.75rem', fontWeight: 700 }}>{selectedRow[4]}</span>
              {selectedRow[1] && <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', background: '#f6f6ff', fontSize: '0.75rem', color: '#535b71' }}>{selectedRow[1]}</span>}
            </div>
            {selectedRow[7] && (
              <div>
                <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#535b71', marginBottom: '0.5rem' }}>Transcript</p>
                <p style={{ fontSize: '0.875rem', lineHeight: 1.7, color: '#272e42', maxHeight: '20rem', overflowY: 'auto' }}>{selectedRow[7]}</p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button className="btn-primary" style={{ flex: 1 }} onClick={() => approveContent.mutate(selectedRow[0])}>✅ Duyệt</button>
              <button className="btn-danger-sm" style={{ flex: 1 }} onClick={() => rejectContent.mutate(selectedRow[0])}>❌ Từ chối</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Bulk CSV Import ──────────────────────────────────────────────────────────

type ImportType = 'questions' | 'content_items' | 'vocabulary'

function downloadErrorReport(errors: any[]) {
  const lines = ['row,field,value,message']
  for (const e of errors) {
    const val = e.value ? `"${String(e.value).replace(/"/g, '""')}"` : ''
    const msg = `"${String(e.message).replace(/"/g, '""')}"`
    lines.push(`${e.row},"${e.field}",${val},${msg}`)
  }
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `error_report_${Date.now()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const IMPORT_TYPE_LABELS: Record<ImportType, string> = {
  questions: '❓ Câu hỏi',
  content_items: '📄 Nội dung',
  vocabulary: '📚 Từ vựng',
}

export function BulkCSVImportTab() {
  const qc = useQueryClient()
  const [importType, setImportType] = useState<ImportType>('questions')
  const [file, setFile] = useState<File | null>(null)
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [validateResult, setValidateResult] = useState<any>(null)
  const [confirmResult, setConfirmResult] = useState<any>(null)
  const [isPending, setIsPending] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`

  function reset() {
    setFile(null); setStep(1); setValidateResult(null); setConfirmResult(null)
  }

  function getAuthHeaders(extra?: Record<string, string>): Record<string, string> {
    const token = useAuthStore.getState().token
    return {
      'ngrok-skip-browser-warning': '1',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra,
    }
  }

  async function handleValidate() {
    if (!file) return
    setIsPending(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('type', importType)
      const res = await fetch(`${BASE}/admin/import/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
      })
      const data = await res.json()
      if (!data.success) { setValidateResult({ error: data.error }); return }
      setValidateResult(data.data)
      setStep(2)
    } catch (e: any) {
      setValidateResult({ error: e.message })
    } finally {
      setIsPending(false)
    }
  }

  async function handleConfirm() {
    if (!validateResult?.import_token) return
    setIsPending(true)
    try {
      const res = await fetch(`${BASE}/admin/import/confirm`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ import_token: validateResult.import_token, type: importType }),
      })
      const data = await res.json()
      setConfirmResult(data.success ? data.data : { error: data.error })
      setStep(3)
      qc.invalidateQueries({ queryKey: ['admin-content'] })
      qc.invalidateQueries({ queryKey: ['admin-questions'] })
    } catch (e: any) {
      setConfirmResult({ error: e.message })
      setStep(3)
    } finally {
      setIsPending(false)
    }
  }

  async function downloadTemplate() {
    try {
      const res = await fetch(`${BASE}/admin/import/template/${importType}`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `template_${importType}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      alert(`Không thể tải template: ${e.message}`)
    }
  }

  const previewKeys = validateResult?.preview?.[0] ? Object.keys(validateResult.preview[0]) : []

  return (
    <div className="admin-tab-panel">
      <h2>Bulk CSV Import</h2>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', alignItems: 'center' }}>
        {([1, 2, 3] as const).map(s => (
          <span key={s} style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.8125rem', fontWeight: 700, background: step === s ? '#004be2' : step > s ? '#006571' : '#eef0ff', color: step >= s ? 'white' : '#a5adc6' }}>
            {s === 1 ? '1. Upload' : s === 2 ? '2. Preview' : '3. Kết quả'}
          </span>
        ))}
      </div>

      {/* ── Step 1: Select type + upload ── */}
      {step === 1 && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {(Object.keys(IMPORT_TYPE_LABELS) as ImportType[]).map(t => (
              <button key={t} onClick={() => { setImportType(t); setFile(null) }}
                className={importType === t ? 'btn-primary' : 'btn-secondary'}>
                {IMPORT_TYPE_LABELS[t]}
              </button>
            ))}
          </div>

          <div className="admin-form" style={{ marginBottom: '1.5rem' }}>
            <button className="btn-secondary" onClick={downloadTemplate}>📥 Tải template CSV ({importType})</button>
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            style={{ border: '2px dashed #d1dcff', borderRadius: '1.25rem', padding: '3rem', textAlign: 'center', cursor: 'pointer', background: file ? 'rgba(0,75,226,0.04)' : 'white', transition: 'all 0.2s', marginBottom: '1.5rem' }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.name.endsWith('.csv')) setFile(f) }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: file ? '#004be2' : '#a5adc6', display: 'block', marginBottom: '0.75rem', fontVariationSettings: "'FILL' 1" }}>upload_file</span>
            {file ? (
              <div>
                <p style={{ fontWeight: 700, color: '#004be2', marginBottom: '0.25rem' }}>{file.name}</p>
                <p style={{ fontSize: '0.875rem', color: '#535b71' }}>{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div>
                <p style={{ fontWeight: 700, color: '#272e42', marginBottom: '0.25rem' }}>Kéo thả file CSV vào đây</p>
                <p style={{ fontSize: '0.875rem', color: '#535b71' }}>hoặc click để chọn file (tối đa 5MB, 500 dòng)</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => setFile(e.target.files?.[0] ?? null)} />
          </div>

          <button className="btn-primary" onClick={handleValidate} disabled={!file || isPending}>
            {isPending ? '⏳ Đang kiểm tra...' : '🔍 Validate CSV'}
          </button>

          {validateResult?.error && (
            <p style={{ color: '#b41340', fontWeight: 700, marginTop: '1rem' }}>❌ {validateResult.error}</p>
          )}
        </>
      )}

      {/* ── Step 2: Preview + errors ── */}
      {step === 2 && validateResult && (
        <>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: '#006571' }}>✅ Hợp lệ: {validateResult.valid}</span>
            <span style={{ fontWeight: 700, color: validateResult.errors?.length ? '#b41340' : '#a5adc6' }}>❌ Lỗi: {validateResult.errors?.length ?? 0}</span>
            <span style={{ color: '#535b71', fontSize: '0.875rem' }}>Tổng: {validateResult.total} dòng</span>
            {validateResult.errors?.length > 0 && (
              <button className="btn-secondary" style={{ fontSize: '0.8125rem' }} onClick={() => downloadErrorReport(validateResult.errors)}>
                📥 Tải error_report.csv
              </button>
            )}
          </div>

          {validateResult.errors?.length > 0 && (
            <div style={{ background: 'rgba(180,19,64,0.05)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1.5rem', maxHeight: '10rem', overflowY: 'auto' }}>
              <p style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.5rem', color: '#b41340' }}>Chi tiết lỗi:</p>
              {validateResult.errors.map((err: any, i: number) => (
                <div key={i} style={{ fontSize: '0.8125rem', color: '#b41340', padding: '0.25rem 0', borderBottom: '1px solid rgba(180,19,64,0.08)' }}>
                  Dòng {err.row} · <strong>{err.field}</strong>{err.value ? ` = "${err.value}"` : ''} — {err.message}
                </div>
              ))}
            </div>
          )}

          {validateResult.valid > 0 && (
            <>
              <p style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.875rem' }}>Preview ({validateResult.valid} dòng hợp lệ):</p>
              <div style={{ overflowX: 'auto', marginBottom: '1.5rem', maxHeight: '20rem', overflowY: 'auto', borderRadius: '0.75rem', border: '1px solid #e8eaf0' }}>
                <table className="admin-table" style={{ minWidth: '100%', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr>{previewKeys.map(k => <th key={k}>{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {validateResult.preview.slice(0, 20).map((row: any, i: number) => (
                      <tr key={i}>{previewKeys.map(k => <td key={k} style={{ maxWidth: '12rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row[k] ?? ''}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn-secondary" onClick={reset}>← Quay lại</button>
            {validateResult.valid > 0 && (
              <button className="btn-primary" onClick={handleConfirm} disabled={isPending}>
                {isPending ? '⏳ Đang import...' : `🚀 Confirm Import (${validateResult.valid} dòng)`}
              </button>
            )}
          </div>
        </>
      )}

      {/* ── Step 3: Result ── */}
      {step === 3 && confirmResult && (
        <>
          <div style={{ background: confirmResult.error ? 'rgba(180,19,64,0.06)' : 'rgba(0,101,113,0.06)', borderRadius: '1rem', padding: '1.5rem', border: `1px solid ${confirmResult.error ? '#b41340' : '#006571'}30`, marginBottom: '1.5rem' }}>
            {confirmResult.error ? (
              <p style={{ color: '#b41340', fontWeight: 700 }}>❌ Lỗi: {confirmResult.error}</p>
            ) : (
              <p style={{ color: '#006571', fontWeight: 700 }}>
                ✅ Import thành công: {confirmResult.inserted} / {confirmResult.total} dòng
              </p>
            )}
          </div>
          <button className="btn-secondary" onClick={reset}>🔄 Import thêm</button>
        </>
      )}
    </div>
  )
}

// ─── Coupon Manager (standalone tab) ─────────────────────────────────────────

export function CouponManagerTab() {
  const qc = useQueryClient()
  const [form, setForm] = useState({ code: '', discountType: 'percent', discountValue: 20, maxUses: '', expiresAt: '' })

  const { data: coupons, isLoading } = useQuery({ queryKey: ['coupons'], queryFn: () => adminAPI.getCoupons() })

  const createCoupon = useMutation({
    mutationFn: () => adminAPI.createCoupon({
      ...form,
      discountValue: Number(form.discountValue),
      maxUses: form.maxUses ? Number(form.maxUses) : undefined,
      expiresAt: form.expiresAt || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coupons'] })
      setForm({ code: '', discountType: 'percent', discountValue: 20, maxUses: '', expiresAt: '' })
    },
  })

  const toggleCoupon = useMutation({
    mutationFn: (code: string) => adminAPI.toggleCoupon(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  })

  const deleteCoupon = useMutation({
    mutationFn: (code: string) => adminAPI.deleteCoupon(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  })

  return (
    <div className="admin-tab-panel">
      <h2>Coupon Manager</h2>

      <div className="admin-form" style={{ marginBottom: '2rem' }}>
        <h3>Tạo coupon mới</h3>
        <div className="form-grid">
          <label>Mã coupon *
            <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SUMMER2026" />
          </label>
          <label>Loại giảm giá
            <select value={form.discountType} onChange={e => setForm({ ...form, discountType: e.target.value })}>
              <option value="percent">Phần trăm (%)</option>
              <option value="fixed">Cố định (VND)</option>
            </select>
          </label>
          <label>Giá trị
            <input type="number" value={form.discountValue} onChange={e => setForm({ ...form, discountValue: Number(e.target.value) })} />
          </label>
          <label>Số lần dùng tối đa (để trống = không giới hạn)
            <input type="number" value={form.maxUses} onChange={e => setForm({ ...form, maxUses: e.target.value })} placeholder="100" />
          </label>
          <label>Hết hạn (để trống = không hết hạn)
            <input type="date" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} />
          </label>
        </div>

        {form.code && (
          <div style={{ background: '#eef0ff', borderRadius: '0.875rem', padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900, fontSize: '1.25rem', color: '#004be2', letterSpacing: '0.05em' }}>{form.code}</span>
            <span style={{ color: '#535b71', fontSize: '0.875rem' }}>
              Giảm {form.discountValue}{form.discountType === 'percent' ? '%' : ' VND'}
              {form.maxUses ? ` · Tối đa ${form.maxUses} lần` : ' · Không giới hạn'}
              {form.expiresAt ? ` · Hết hạn ${new Date(form.expiresAt).toLocaleDateString('vi')}` : ''}
            </span>
          </div>
        )}

        <button className="btn-primary" onClick={() => createCoupon.mutate()} disabled={!form.code || createCoupon.isPending}>
          {createCoupon.isPending ? 'Đang tạo...' : '+ Tạo coupon'}
        </button>
      </div>

      {isLoading ? <div className="loading">Đang tải...</div> : (
        <table className="admin-table">
          <thead>
            <tr><th>Code</th><th>Loại</th><th>Giá trị</th><th>Đã dùng</th><th>Tối đa</th><th>Hết hạn</th><th>Trạng thái</th><th></th></tr>
          </thead>
          <tbody>
            {(coupons as any[] ?? []).map((r: any, i: number) => {
              const isActive = r[8]
              const usageRatio = r[3] ? (r[4] / r[3]) * 100 : 0
              return (
                <tr key={i}>
                  <td><strong style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.05em' }}>{r[0]}</strong></td>
                  <td>{r[1] === 'percent' ? '%' : 'VND'}</td>
                  <td>{r[2]}{r[1] === 'percent' ? '%' : ' VND'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{r[4]}</span>
                      {r[3] && (
                        <div style={{ width: '4rem', height: '0.375rem', background: '#eef0ff', borderRadius: '9999px', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, usageRatio)}%`, height: '100%', background: usageRatio > 80 ? '#b41340' : '#004be2', borderRadius: '9999px' }} />
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{r[3] ?? '∞'}</td>
                  <td>{r[7] ? new Date(r[7]).toLocaleDateString('vi') : '∞'}</td>
                  <td>
                    <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700, background: isActive ? 'rgba(0,101,113,0.1)' : 'rgba(165,173,198,0.2)', color: isActive ? '#006571' : '#a5adc6' }}>
                      {isActive ? 'Active' : 'Tắt'}
                    </span>
                  </td>
                  <td className="action-cell">
                    <button className="btn-xs btn-warning" onClick={() => toggleCoupon.mutate(r[0])}>
                      {isActive ? 'Tắt' : 'Bật'}
                    </button>
                    <button className="btn-xs btn-danger" onClick={() => { if (confirm(`Xóa coupon ${r[0]}?`)) deleteCoupon.mutate(r[0]) }}>Xóa</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
